import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, ViewEncapsulation } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { DataService } from "src/app/Services/dataservice.service";
import { FlowServiceService } from "src/app/Services/flow-service.service";
import { EventEmitterService } from "src/app/Services/event-emitter.service";
import { AuthModalService } from "src/app/Services/auth-modal.service";
import { TokenService } from "src/app/Services/token.service";
import { UserserviceService } from "src/app/Services/userservice.service";
import { ThemePreferenceService, ThemeMode } from "src/app/Services/theme-preference.service";
import { Subscription } from "rxjs";

@Component({
  selector: "app-how-it-works",
  templateUrl: "./how-it-works.component.html",
  styleUrls: ["./how-it-works.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class HowItWorksComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly themeStorageKey = "selectedTheme";
  selectedThemeMode: "light" | "dark" = "light";
  private themeSubscription?: Subscription;
  @ViewChild('demoVideo', { static: false }) demoVideoRef?: ElementRef<HTMLVideoElement>;
  isScrolled = false;
  isMobileView = false;
  activeSection = 1;
  isVideoModalOpen = false;
  demoVideoUrl = '';
  /** Old YouTube link for Brands & Retailers – replace with your actual YouTube watch URL if needed. */
  private readonly brandsYoutubeUrl = 'https://easycmstest.azureedge.net/mediavideo/65d1dc7f3b89496592d3824d169c5987.mp4';
  private defaultDemoVideoUrl = 'https://flowcheststorage.blob.core.windows.net/mediavideo/21c76aebc15c4506b0442388b66bd812.mp4'; // Watch demo (hero)
  private stakeholderVideoUrls: Record<'creators' | 'brands' | 'makers', string> = {
    creators: 'https://flowcheststorage.blob.core.windows.net/mediavideo/5fd235f265b44dfb98b76c85108ca633.mp4', // Creator
    brands: this.brandsYoutubeUrl, // Brands & Retailers – old YouTube link (update brandsYoutubeUrl above)
    makers: 'https://flowcheststorage.blob.core.windows.net/mediavideo/29dbe6df4ce34e6097606bd1c9b90480.mp4'   // Makers & Learners
  };
  private scrollListener?: () => void;
  private resizeListener?: () => void;
  private scrollAnimationFrameId?: number;
  private intersectionObserver?: IntersectionObserver;
  private scrollPositionWhenModalOpened = 0;
  navigationItems = [
    { id: 'hero', label: 'Overview' },
    { id: 'six-step-flow', label: '6-Step Flow' },
    { id: 'step1', label: 'Start Project' },
    { id: 'step2', label: 'Break Steps' },
    { id: 'step3', label: 'Add Items' },
    { id: 'step4', label: 'Follow Real Time' },
    { id: 'step5', label: 'Complete Build' },
    { id: 'step6', label: 'Learn' },
    { id: 'stakeholders', label: 'Stakeholders' },
    { id: 'sustainability', label: 'Sustainability' },
    { id: 'precision', label: 'Precision' },
    { id: 'closing', label: 'CTA' }
  ];

  constructor(
    private router: Router,
    private dataService: DataService,
    private flowServiceService: FlowServiceService,
    private eventEmitterService: EventEmitterService,
    private authModalService: AuthModalService,
    private tokenService: TokenService,
    private sanitizer: DomSanitizer,
    private userserviceService: UserserviceService,
    private themePreferenceService: ThemePreferenceService
  ) {}

  ngOnInit(): void {
    this.themeSubscription = this.themePreferenceService.themeChanges$.subscribe((theme) => {
      this.selectedThemeMode = theme;
    });
    this.initializeThemeMode();
    // Initialize the default demo video URL (used by hero Watch Demo)
    this.demoVideoUrl = this.defaultDemoVideoUrl;
    this.flowServiceService.setScrollTop();
    this.checkMobileView();
    if (typeof window !== 'undefined') {
      this.resizeListener = () => this.checkMobileView();
      window.addEventListener('resize', this.resizeListener);
    }
    this.eventEmitterService.callEvent();
    this.eventEmitterService.setHomePageIconColorEvent("home");
    this.setupScrollTracking();
  }

  checkMobileView(): void {
    if (typeof window !== 'undefined') {
      this.isMobileView = window.innerWidth <= 768;
    }
  }

  toggleSidebar(): void {
    // Access the app component's sidebar through DOM or service
    // Since sidebar is controlled by app component, we'll trigger it via custom event
    const event = new CustomEvent('toggleSidebar');
    window.dispatchEvent(event);
    
    // Also try to directly access the app component if available
    const appComponent = (window as any).appComponent;
    if (appComponent && appComponent.toggleSidebar) {
      appComponent.toggleSidebar();
    }
  }

  get isDarkThemeActive(): boolean {
    return this.selectedThemeMode === "dark";
  }

  toggleThemeMode(): void {
    const nextTheme = this.themePreferenceService.toggleTheme();
    this.persistThemeMode(nextTheme);
  }

  private persistThemeMode(theme: "light" | "dark"): void {
    const userData = JSON.parse(localStorage.getItem("LoggedInUser") || "null");
    if (userData?.emailAddress) {
      this.userserviceService
        .UpdateThemeSettingsForCustomerProfileDetails({
          emailAddress: userData.emailAddress,
          selectedTheme: theme,
        })
        .subscribe({
          next: () => {
            userData.selectedTheme = theme;
            localStorage.setItem("LoggedInUser", JSON.stringify(userData));
          },
          error: () => {},
        });
    }
  }

  private initializeThemeMode(): void {
    const theme = this.themePreferenceService.initializeFromStorage();
    this.selectedThemeMode = theme;
  }

  homePage(): void {
    this.router.navigate(["/"]);
  }

  setupScrollTracking(): void {
    if (typeof window !== 'undefined') {
      // Throttle scroll events using requestAnimationFrame for better performance
      this.scrollListener = () => {
        if (this.scrollAnimationFrameId) {
          cancelAnimationFrame(this.scrollAnimationFrameId);
        }
        this.scrollAnimationFrameId = requestAnimationFrame(() => {
          this.isScrolled = window.scrollY > 50;
          this.updateActiveSection();
        });
      };
      window.addEventListener('scroll', this.scrollListener, { passive: true });
      
      this.setupScrollAnimations();
    }
  }

  setupScrollAnimations(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      const elements = document.querySelectorAll('.section, .step-card, .feature-card');
      elements.forEach(el => el.classList.add('animate-in'));
      return;
    }

    const observerOptions: IntersectionObserverInit = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          this.intersectionObserver?.unobserve(entry.target);
        }
      });
    }, observerOptions);

    setTimeout(() => {
      const sections = document.querySelectorAll('.section, .step-card, .feature-card');
      sections.forEach(section => {
        this.intersectionObserver?.observe(section);
      });
    }, 100);
  }

  updateActiveSection(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    // Cache section elements to avoid repeated DOM queries
    const headerHeight = 64; // Page header height
    const scrollPosition = window.scrollY + headerHeight + 50;

    // Only query DOM once per update
    for (let i = this.navigationItems.length - 1; i >= 0; i--) {
      const section = document.getElementById(this.navigationItems[i].id);
      if (section && section.offsetTop <= scrollPosition) {
        // Only update if changed to avoid unnecessary change detection
        if (this.activeSection !== i + 1) {
          this.activeSection = i + 1;
        }
        break;
      }
    }
  }

  scrollToSection(sectionId: string): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 64; // Page header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }

  isUserLoggedIn(): boolean {
    const refreshToken = this.tokenService.getRefreshToken();
    const userData = localStorage.getItem("LoggedInUser");
    return !!(refreshToken && userData && userData !== 'null');
  }

  navigateToSignUp(): void {
    if (this.isUserLoggedIn()) {
      // If logged in, navigate to home page (Explore action - Watch Demo is explore)
      this.router.navigate(['/']);
    } else {
      // If not logged in, show sign-up modal
      this.authModalService.openModal('registration');
    }
  }

  navigateToGetStarted(): void {
    if (this.isUserLoggedIn()) {
      // If logged in, navigate to create-flow page (Build action)
      this.router.navigate(['/create-guide']);
    } else {
      // If not logged in, show sign-up modal
      this.authModalService.openModal('registration');
    }
  }

  openVideoModal(videoKey?: 'creators' | 'brands' | 'makers'): void {
    // If a specific stakeholder video is requested, switch the URL accordingly.
    if (videoKey) {
      const url = this.stakeholderVideoUrls[videoKey];
      if (url) {
        this.demoVideoUrl = url;
      }
    } else {
      // Fallback to default demo video for generic "Watch Demo" actions (e.g. hero button)
      this.demoVideoUrl = this.defaultDemoVideoUrl;
    }

    this.isVideoModalOpen = true;
    // Keep current scroll position so modal opens in view without scrolling
    if (typeof window !== 'undefined') {
      this.scrollPositionWhenModalOpened = window.scrollY || window.pageYOffset;
    }
    // Prevent body scroll when modal is open
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden';
    }
    // Ensure iOS inline playback after modal opens (only for native video, not YouTube)
    if (!this.isYoutubeUrl(this.demoVideoUrl)) {
      setTimeout(() => this.setupIOSVideoPlayback(), 100);
    }
  }

  isYoutubeUrl(url: string): boolean {
    if (!url) return false;
    return /youtube\.com\/watch|youtu\.be\//i.test(url);
  }

  getYoutubeEmbedUrl(url: string): string {
    if (!this.isYoutubeUrl(url)) return '';
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    const id = match ? match[1] : '';
    return id ? `https://www.youtube.com/embed/${id}?autoplay=1` : '';
  }

  getYoutubeEmbedSafeUrl(url: string): SafeResourceUrl {
    const embed = this.getYoutubeEmbedUrl(url);
    return embed ? this.sanitizer.bypassSecurityTrustResourceUrl(embed) : this.sanitizer.bypassSecurityTrustResourceUrl('about:blank');
  }

  setupIOSVideoPlayback(): void {
    if (this.demoVideoRef?.nativeElement) {
      const video = this.demoVideoRef.nativeElement;
      // Ensure iOS-specific attributes are set
      video.setAttribute('playsinline', 'true');
      video.setAttribute('webkit-playsinline', 'true');
      video.setAttribute('x5-playsinline', 'true'); // For some Android browsers
      // Play with sound when user has opened the modal
      video.muted = false;
      video.play().catch(() => {
        // Ignore autoplay errors; user can tap to play
      });
    }
  }

  ngAfterViewInit(): void {
    // Setup video when view is initialized
    if (this.isVideoModalOpen) {
      this.setupIOSVideoPlayback();
    }
  }

  closeVideoModal(): void {
    this.isVideoModalOpen = false;
    // Restore body scroll when modal is closed
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
    // Restore scroll position so page stays where it was (no scroll up)
    if (typeof window !== 'undefined' && this.scrollPositionWhenModalOpened !== undefined) {
      window.scrollTo(0, this.scrollPositionWhenModalOpened);
    }
  }

  ngOnDestroy(): void {
    this.themeSubscription?.unsubscribe();
    // Clean up scroll listener
    if (this.scrollListener && typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.scrollListener);
    }
    
    // Cancel any pending animation frame
    if (this.scrollAnimationFrameId) {
      cancelAnimationFrame(this.scrollAnimationFrameId);
    }
    
    // Clean up resize listener
    if (this.resizeListener && typeof window !== 'undefined') {
      window.removeEventListener('resize', this.resizeListener);
    }
    
    // Clean up IntersectionObserver
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }
}

